import React from 'react';
import styled from 'styled-components';

const Card = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 0.25rem;
  background: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardBody = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.35rem;
  padding: 0.35rem;
`;

const FormGroup = styled.div`
  margin: 0;
`;

const FormLabel = styled.label`
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 0.12rem;
  font-size: 0.72rem;
  display: block;
`;

const FormControl = styled.input`
  padding: 0.3rem 0.4rem;
  border: 1px solid #ccc;
  background-color: white;
  font-size: 0.95rem;
  width: 100%;
  border-radius: 2px;

  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const FormSelect = styled.select`
  padding: 0.3rem 0.4rem;
  border: 1px solid #ccc;
  background-color: white;
  font-size: 0.95rem;
  width: 100%;
  border-radius: 2px;

  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Row = styled.div`
  display: contents;
`;

const Col = styled.div`
  min-width: 0;
`;


interface ConfigurationProps {
  market: string;
  contractType: string;
  ticks: number;
  overValue: number;
  underValue: number;
  onMarketChange: (market: string) => void;
  onContractTypeChange: (type: string) => void;
  onTicksChange: (ticks: number) => void;
  onOverUnderChange: (over: number, under: number) => void;
}

const Configuration: React.FC<ConfigurationProps> = ({
  market,
  contractType,
  ticks,
  overValue,
  underValue,
  onMarketChange,
  onContractTypeChange,
  onTicksChange,
  onOverUnderChange,
}) => {
  const markets = [
    { value: 'R_10', label: 'Volatility 10' },
    { value: '1HZ10V', label: 'Volatility 10 (1s)' },
    { value: '1HZ15V', label: 'Volatility 15 (1s)' },
    { value: 'R_25', label: 'Volatility 25' },
    { value: '1HZ25V', label: 'Volatility 25 (1s)' },
    { value: '1HZ30V', label: 'Volatility 30 (1s)' },
    { value: 'R_50', label: 'Volatility 50' },
    { value: '1HZ50V', label: 'Volatility 50 (1s)' },
    { value: 'R_75', label: 'Volatility 75' },
    { value: '1HZ75V', label: 'Volatility 75 (1s)' },
    { value: '1HZ90V', label: 'Volatility 90 (1s)' },
    { value: 'R_100', label: 'Volatility 100' },
    { value: '1HZ100V', label: 'Volatility 100 (1s)' },
    { value: 'JD10', label: 'Jump Index 10' },
    { value: 'JD25', label: 'Jump Index 25' },
    { value: 'JD50', label: 'Jump Index 50' },
    { value: 'JD75', label: 'Jump Index 75' },
    { value: 'JD100', label: 'Jump Index 100' },
    { value: 'RDBEAR', label: 'Bear Market' },
    { value: 'RDBULL', label: 'Bull Market' },
  ];

  const contractTypes = [
    { value: '0', label: 'Even/Odd' },
    { value: '1', label: 'Rise/Fall' },
    { value: '2', label: 'Over/Under' },
    { value: '3', label: 'Digit Frequency' },
  ];

  return (
    <Card>
      <CardBody>
        <Row>
          <Col>
            <FormGroup>
              <FormLabel htmlFor="market">Synthetic Market</FormLabel>
              <FormSelect
                id="market"
                value={market}
                onChange={(e) => onMarketChange(e.target.value)}
              >
                {markets.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </FormSelect>
            </FormGroup>
          </Col>
          <Col>
            <FormGroup>
              <FormLabel htmlFor="contract_type">Trade Type</FormLabel>
              <FormSelect
                id="contract_type"
                value={contractType}
                onChange={(e) => onContractTypeChange(e.target.value)}
              >
                {contractTypes.map((ct) => (
                  <option key={ct.value} value={ct.value}>
                    {ct.label}
                  </option>
                ))}
              </FormSelect>
            </FormGroup>
          </Col>
        </Row>

        {contractType === '2' && (
          <Row>
            <Col>
              <FormGroup>
                <FormLabel htmlFor="over_input">Over Value</FormLabel>
                <FormControl
                  type="number"
                  id="over_input"
                  step="1"
                  value={overValue}
                  min="0"
                  max="9"
                  onChange={(e) => onOverUnderChange(parseInt(e.target.value), underValue)}
                  required
                />
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <FormLabel htmlFor="under_input">Under Value</FormLabel>
                <FormControl
                  type="number"
                  id="under_input"
                  step="1"
                  value={underValue}
                  min="0"
                  max="9"
                  onChange={(e) => onOverUnderChange(overValue, parseInt(e.target.value))}
                  required
                />
              </FormGroup>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <FormGroup>
              <FormLabel htmlFor="ticks">Number of Ticks to Analyze</FormLabel>
              <FormControl
                type="number"
                id="ticks"
                step="1"
                value={ticks}
                min="1"
                max="5000"
                onChange={(e) => onTicksChange(parseInt(e.target.value))}
                required
              />
            </FormGroup>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default Configuration;
